// PRD §7.5 — Founder side of the engagement workspace.
// Founder is read-only on notes + time logs; SUBMITS progress updates against
// the mission (mentor reacts to them, mentor isn't a vendor); CONFIRMS
// completion when the mentor has marked complete.
import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { ArrowLeft, FileText, Clock, CheckCircle2, MessageSquare, AlertCircle, Video, PlayCircle, CalendarPlus } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";
import { RecordingNotice, HowMentorCallsWork } from "@/components/mentor-call-guide";
import { fp, useCohort } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

export default function FounderEngagementDetail() {
  const cohort = useCohort();
  const [, params] = useRoute(`/${cohort.slug}/founder/engagements/:id`);
  const id = Number(params?.id);
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function refresh() {
    fetch(`${BASE}api/founder-portal/engagements/${id}`, { headers: auth() })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(setData).catch(e => setErr(String(e)));
  }
  useEffect(() => { if (Number.isFinite(id)) refresh(); }, [id]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link href={fp(cohort.slug, "mentors")} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Mentors</Link>
      <div className="text-sm text-red-400" data-testid="error-engagement">{err}</div>
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-10"><ProgressLoader active={true} title="Loading" stages={["Loading engagement"]} /></div>;

  const { engagement, mission, mentor, notes, deliverables, timeLogs, calls = [] } = data;
  const totalHours = timeLogs.reduce((s: number, t: any) => s + Number(t.hours || 0), 0);

  async function submitProgress(title: string, description: string, artifactUrl: string) {
    if (!title.trim()) return;
    setBusy("progress");
    try {
      const r = await fetch(`${BASE}api/founder-portal/engagements/${id}/deliverables`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ title, description, artifactUrl }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  async function scheduleCall(callId: number, startUtcIso: string, durationMin: number, agenda: string) {
    setBusy(`schedule-${callId}`);
    try {
      const r = await fetch(`${BASE}api/founder-portal/intro-calls/${callId}/schedule`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ startUtcIso, durationMin, agenda: agenda || undefined }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  async function confirmCompletion() {
    if (!confirm(`Confirm ${mentor.fullName} delivered this mission? This closes the engagement.`)) return;
    setBusy("complete");
    try {
      const r = await fetch(`${BASE}api/founder-portal/engagements/${id}/confirm-completion`, { method: "POST", headers: auth() });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <Link href={fp(cohort.slug, "mentors")} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Mentors</Link>

      {/* Mission + mentor header */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-5" data-testid="mission-header">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-xl font-bold">{mission.title}</h1>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300 whitespace-nowrap">{engagement.status}</span>
        </div>
        <div className="text-sm text-white/85 mb-1">Mentor: <Link href={fp(cohort.slug, `mentors/${mentor.id}`)} className="font-bold underline hover:text-white" data-testid={`link-mentor-${mentor.id}`}>{mentor.fullName}</Link>{mentor.headline ? ` — ${mentor.headline}` : ""}</div>
        {mentor.expertiseTags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {mentor.expertiseTags.map((t: string) => <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-white/[0.06] text-white/70">{t}</span>)}
          </div>
        )}
        <div className="text-xs text-white/60 mb-3">{mission.deadline ? `Due ${new Date(mission.deadline).toLocaleDateString()}` : "No deadline set"}</div>
        <div className="text-sm mb-2"><span className="text-[10px] uppercase tracking-[0.18em] text-white/60 mr-2">Problem</span>{mission.problemStatement}</div>
        <div className="text-sm"><span className="text-[10px] uppercase tracking-[0.18em] text-white/60 mr-2">Deliverable</span>{mission.deliverable}</div>
      </div>

      {/* Awaiting confirmation banner */}
      {engagement.status === "completed_pending_founder" && (
        <div className="bg-amber-500/10 border border-amber-500/35 rounded-lg p-4 flex items-start justify-between gap-3" data-testid="banner-confirm-completion">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 text-amber-300 flex-shrink-0" />
            <div className="text-sm text-amber-100">
              <strong>{mentor.fullName}</strong> says this mission is complete. Confirm if you agree, or push back by logging another progress update below.
            </div>
          </div>
          <button onClick={confirmCompletion} disabled={busy === "complete"}
            className="bg-emerald-600 hover:bg-emerald-600/90 disabled:opacity-30 text-white text-xs font-semibold rounded px-3 py-1.5 whitespace-nowrap"
            data-testid="button-confirm-completion">
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Confirm complete
          </button>
        </div>
      )}

      {/* Calls (intro + working). Bot owns the Meet event; once the call
          ends the recording-watcher cron writes the Drive playback URL
          back onto googleMeetUrl, so this section flips from Join Meet
          to Watch recording without a schema change. */}
      <Section icon={<Video className="w-3.5 h-3.5" />} title="Calls" testId="section-calls">
        <RecordingNotice scope="founder" />
        <HowMentorCallsWork scope="founder" />
        {busy?.startsWith("schedule-") ? (
          <ProgressLoader active={true} title="Scheduling call" stages={[
            "Reserving the slot",
            "Creating Google Meet event",
            `Sending calendar invites to you and ${mentor.fullName}`,
            "Logging recording-consent acknowledgement",
          ]} note="The Meet link and join button will appear here once Google confirms." />
        ) : (
          <CallsList calls={calls} mentorName={mentor.fullName} busy={busy} onSchedule={scheduleCall} />
        )}
      </Section>

      {/* Progress updates — founder ships, mentor reviews. */}
      <Section icon={<FileText className="w-3.5 h-3.5" />} title="Progress updates" testId="section-deliverables">
        {deliverables.length === 0 ? (
          <Empty>No progress updates yet. Log what you've shipped against this mission so {mentor.fullName} can react.</Empty>
        ) : (
          <div className="divide-y divide-white/10">
            {deliverables.map((d: any) => (
              <ProgressUpdateRow key={d.id} d={d} mentorName={mentor.fullName} />
            ))}
          </div>
        )}
        {busy === "progress" ? (
          <div className="mt-3 pt-3 border-t border-white/10">
            <ProgressLoader active={true} title="Logging update" stages={[
              "Saving your update",
              `Notifying ${mentor.fullName}`,
            ]} />
          </div>
        ) : (
          <ProgressUpdateForm onSubmit={submitProgress} />
        )}
      </Section>

      {/* Notes (read-only) */}
      <Section icon={<MessageSquare className="w-3.5 h-3.5" />} title="Notes from mentor" testId="section-notes">
        {notes.length === 0 ? <Empty>No notes yet.</Empty> : (
          <div className="divide-y divide-white/10">
            {notes.map((n: any) => (
              <div key={n.id} className="py-2.5" data-testid={`note-${n.id}`}>
                <div className="text-[10px] text-white/60 mb-1">{n.authorKind} · {new Date(n.createdAt).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Time logs (read-only) */}
      <Section icon={<Clock className="w-3.5 h-3.5" />} title={`Time logged · ${totalHours.toFixed(2)}h`} testId="section-time">
        {timeLogs.length === 0 ? <Empty>No time logged yet.</Empty> : (
          <div className="divide-y divide-white/10">
            {timeLogs.map((t: any) => (
              <div key={t.id} className="py-2 flex items-baseline justify-between gap-3" data-testid={`time-log-${t.id}`}>
                <div className="text-sm"><strong>{Number(t.hours).toFixed(2)}h</strong> <span className="text-xs text-white/60">· {t.kind}</span>{t.note && <span className="text-xs text-white/60"> — {t.note}</span>}</div>
                <div className="text-[10px] text-white/60">{new Date(t.createdAt).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ icon, title, testId, children }: { icon: React.ReactNode; title: string; testId: string; children: React.ReactNode }) {
  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] text-white/60">{icon}{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }: { children: React.ReactNode }) { return <div className="text-xs text-white/60 italic">{children}</div>; }

// Calls list — founder side. Either party can pick the slot (whichever is
// faster). After the call runs, the recording-watcher cron overwrites
// googleMeetUrl with the Drive playback URL, so the same field powers
// "Join Meet" while scheduled and "Watch recording" once completed.
function CallsList({ calls, mentorName, busy, onSchedule }: {
  calls: any[]; mentorName: string; busy: string | null;
  onSchedule: (callId: number, startUtcIso: string, durationMin: number, agenda: string) => void;
}) {
  if (!calls || calls.length === 0) {
    return <Empty>No calls yet. When {mentorName} or you picks an intro slot, it will appear here with a Meet link.</Empty>;
  }
  return (
    <div className="divide-y divide-white/10">
      {calls.map((c: any) => {
        const isPending = c.status === "pending_slot";
        const isScheduled = c.status === "scheduled";
        const isCompleted = c.status === "completed";
        const isCancelled = c.status === "cancelled" || c.status === "no_show";
        const when = c.scheduledStartUtc ? new Date(c.scheduledStartUtc) : null;
        return (
          <div key={c.id} className="py-3" data-testid={`call-${c.id}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <div className="text-sm font-medium capitalize">{c.kind} call</div>
                {when && <div className="text-xs text-white/60 mt-0.5">{when.toLocaleString()} · {c.durationMin || 45} min</div>}
                {!when && isPending && <div className="text-xs text-white/60 mt-0.5">Slot not picked yet</div>}
              </div>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded whitespace-nowrap ${
                isCompleted ? "bg-emerald-500/15 text-emerald-300"
                : isScheduled ? "bg-sky-500/15 text-sky-300"
                : isCancelled ? "bg-zinc-500/15 text-zinc-400"
                : "bg-amber-500/15 text-amber-300"
              }`} data-testid={`call-status-${c.id}`}>{c.status.replace("_", " ")}</span>
            </div>
            {c.agenda && <div className="text-xs text-white/65 mt-1 whitespace-pre-wrap">{c.agenda}</div>}
            {isScheduled && c.googleMeetUrl && (
              <a href={c.googleMeetUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#FF5C00] hover:underline"
                data-testid={`call-join-${c.id}`}>
                <Video className="w-3 h-3" /> Join Meet
              </a>
            )}
            {isCompleted && c.googleMeetUrl?.includes("drive.google.com") && (
              <a href={c.googleMeetUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#FF5C00] hover:underline"
                data-testid={`call-recording-${c.id}`}>
                <PlayCircle className="w-3 h-3" /> Watch recording
              </a>
            )}
            {isCompleted && !c.googleMeetUrl?.includes("drive.google.com") && (
              <div className="mt-2 text-[11px] text-white/60 italic" data-testid={`call-no-recording-${c.id}`}>
                Recording not yet available. The watcher checks every 10 minutes after a call ends.
              </div>
            )}
            {isCancelled && (
              <div className="mt-2 text-[11px] text-white/60">
                Cancelled{c.cancelledByKind ? ` by ${c.cancelledByKind}` : ""}{c.cancellationReason ? ` — ${c.cancellationReason}` : ""}
              </div>
            )}
            {isPending && (
              <FounderCallScheduleForm
                callId={c.id}
                defaultDuration={c.durationMin || 45}
                defaultAgenda={c.agenda || ""}
                disabled={busy === `schedule-${c.id}`}
                mentorName={mentorName}
                onSubmit={onSchedule}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FounderCallScheduleForm({ callId, defaultDuration, defaultAgenda, disabled, mentorName, onSubmit }: {
  callId: number; defaultDuration: number; defaultAgenda: string; disabled: boolean; mentorName: string;
  onSubmit: (callId: number, startUtcIso: string, durationMin: number, agenda: string) => void;
}) {
  const [when, setWhen] = useState("");
  const [dur, setDur] = useState(String(defaultDuration));
  const [agenda, setAgenda] = useState(defaultAgenda);
  const [ack, setAck] = useState(false);
  const minLocal = (() => { const d = new Date(Date.now() + 15 * 60 * 1000); d.setSeconds(0, 0); return d.toISOString().slice(0, 16); })();
  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!when || !ack) return;
    const startUtcIso = new Date(when).toISOString();
    onSubmit(callId, startUtcIso, Number(dur) || 45, agenda.trim());
  }
  return (
    <form onSubmit={submit} className="mt-3 space-y-2 bg-black/30 border border-white/10 rounded-md p-3" data-testid={`call-form-${callId}`}>
      <div className="text-[11px] text-white/65">Pick a slot. {mentorName} will get a calendar invite + Meet link. Calls are recorded; the recording lands here when it ends.</div>
      <div className="flex flex-wrap gap-2">
        <input type="datetime-local" value={when} min={minLocal} onChange={e => setWhen(e.target.value)} required
          className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50"
          data-testid={`input-call-when-${callId}`} />
        <select value={dur} onChange={e => setDur(e.target.value)}
          className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50"
          data-testid={`select-call-duration-${callId}`}>
          <option value="30">30 min</option><option value="45">45 min</option><option value="60">60 min</option>
        </select>
      </div>
      <input value={agenda} onChange={e => setAgenda(e.target.value)} placeholder="Agenda (optional)"
        className="w-full bg-black/30 border border-white/10 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50"
        data-testid={`input-call-agenda-${callId}`} />
      <label className="flex items-start gap-2 text-[11px] text-white/65 cursor-pointer">
        <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)}
          className="mt-0.5 accent-[#FF5C00]" data-testid={`check-consent-${callId}`} />
        <span>I understand this call will be recorded and made available in the engagement workspace, and I'll let {mentorName} know.</span>
      </label>
      <button type="submit" disabled={disabled || !when || !ack}
        className="inline-flex items-center gap-1 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-30 text-white text-xs font-semibold rounded px-3 py-1.5"
        data-testid={`button-schedule-call-${callId}`}>
        <CalendarPlus className="w-3 h-3" /> {disabled ? "Scheduling…" : "Schedule call"}
      </button>
    </form>
  );
}

// Founder-side row: read-only history of what they shipped, with the
// mentor's latest verdict surfaced so the founder knows where they stand.
function ProgressUpdateRow({ d, mentorName }: { d: any; mentorName: string }) {
  const lastReview = d.reviews[d.reviews.length - 1];
  return (
    <div className="py-3" data-testid={`deliverable-${d.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-medium">{d.title}</div>
          {d.description && <div className="text-xs text-white/65 mt-0.5">{d.description}</div>}
          {d.artifactUrl && <a href={d.artifactUrl} target="_blank" rel="noreferrer" className="text-xs text-[#FF5C00] hover:underline mt-1 inline-block">{d.artifactUrl}</a>}
        </div>
        <div className="text-[10px] text-white/60 whitespace-nowrap">{new Date(d.submittedAt).toLocaleDateString()}</div>
      </div>
      {lastReview ? (
        <div className={`mt-2 text-[11px] px-2 py-1.5 rounded inline-block ${lastReview.decision === "accepted" ? "bg-emerald-500/15 text-emerald-300" : lastReview.decision === "rejected" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
          {mentorName}: <strong>{lastReview.decision === "accepted" ? "approved" : lastReview.decision.replace("_", " ")}</strong>{lastReview.feedback ? ` — ${lastReview.feedback}` : ""}
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-white/60 italic">Awaiting {mentorName}'s review.</div>
      )}
    </div>
  );
}

// Founder logs what they shipped against the mission. Mentor reacts.
function ProgressUpdateForm({ onSubmit }: { onSubmit: (t: string, d: string, u: string) => void }) {
  const [t, setT] = useState(""); const [d, setD] = useState(""); const [u, setU] = useState("");
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(t, d, u); setT(""); setD(""); setU(""); }} className="flex flex-col gap-2 mt-3 pt-3 border-t border-white/10">
      <input value={t} onChange={e => setT(e.target.value)} placeholder="Title (e.g. Pricing model v1)"
        className="w-full bg-black/30 border border-white/10 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50" data-testid="input-deliverable-title" />
      <input value={d} onChange={e => setD(e.target.value)} placeholder="What's in it / what changed (optional)"
        className="w-full bg-black/30 border border-white/10 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50" data-testid="input-deliverable-description" />
      <input value={u} onChange={e => setU(e.target.value)} placeholder="Link (https://… optional)"
        className="w-full bg-black/30 border border-white/10 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50" data-testid="input-deliverable-url" />
      <button type="submit" disabled={!t.trim()} className="self-end bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-30 text-white text-xs font-semibold rounded px-3 py-1.5" data-testid="button-submit-deliverable">Log update</button>
    </form>
  );
}
